'use client'
import Button from '@/app/components/common/Button'
import Title from '@/app/components/common/Title'
import TranslateString from '@/app/components/common/TranslateString'
import TravelInformationBanner from '@/app/components/TravelInformationBanner'
import { Icons } from '@/app/components/ui/Icons'
import {
	FORM_TYPE,
	INPUT,
	REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY,
	REDIRECT_PARTNERS,
	TABLET_VERTICAL,
	XL_BREAKPOINT,
	XLG_BREAKPOINT,
} from '@/app/constants'
import eventBus from '@/app/eventBus'
import { emitSearchHotelsNowGAEvent } from '@/app/ga-event-helpers'
import { PS_EVENTS } from '@/app/ps-events'
import { SearchItem } from '@/app/types'
import {
	getRedirectPartnerInfo,
	smoothScrollTo,
	submitHiddenForm,
} from '@/app/utils'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useParams, usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

const TravelInformation = () => {
	const redirectFormRef = useRef<HTMLFormElement>(null)
	const { lang } = useParams()
	const pathname = usePathname()
	const t = useTranslations('Main.travelInfo')
	const searchParams = useSearchParams()
	const tForm = useTranslations('Form')
	const isXLG = useMediaQuery(XLG_BREAKPOINT)
	const isXL = useMediaQuery(XL_BREAKPOINT)
	const isTabletVertical = useMediaQuery(TABLET_VERTICAL)
	const isMainHotelPage = pathname.search('/mp/') > 0

	const [formData, _] = useLocalStorage<Record<string, any>>(
		REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY
	)
	const [hasSelectedItem, setHasSelectedItem] = useState(false)
	const { backTabPartner } = getRedirectPartnerInfo(searchParams)

	const handleButtonClick = () => {
		emitSearchHotelsNowGAEvent()

		if (hasSelectedItem && isMainHotelPage) {
			const formDataSend = {
				...formData,
				formType: FORM_TYPE.BACK,
				backTabPartner: backTabPartner || REDIRECT_PARTNERS.BOOKING,
			}
			submitHiddenForm(formDataSend, redirectFormRef.current as HTMLFormElement)
		} else {
			const bookingFormRef = document.querySelector('.booking')
			if (bookingFormRef) {
				smoothScrollTo(bookingFormRef.getBoundingClientRect().top)

				setTimeout(() => {
					eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.LOCATION)
					eventBus.publish(PS_EVENTS.FIELD_ERROR, INPUT.LOCATION)
				}, 500)
			} else {
				console.error('Booking form not found')
			}
		}
	}

	const getSearchText = () => {
		const shouldShowShort = lang === 'fr' || lang === 'de'
		if (shouldShowShort && (isTabletVertical || (isXL && !isXLG))) {
			return tForm('newDesign.searchButton')
		}
		return isXLG && !shouldShowShort ? t('searchHotelsNow') : t('searchHotels')
	}

	useEffect(() => {
		const handleSelectedItemChange = (data: {
			hasSelectedItem: boolean
			selectedItem: SearchItem | null
		}) => {
			setHasSelectedItem(data.hasSelectedItem)
		}

		eventBus.subscribe(
			PS_EVENTS.SELECTED_ITEM_CHANGED,
			handleSelectedItemChange
		)

		return () => {
			eventBus.unsubscribe(
				PS_EVENTS.SELECTED_ITEM_CHANGED,
				handleSelectedItemChange
			)
		}
	}, [])

	return (
		<div className="travel-info flex gap-4">
			<div className="max-w-[454px] flex-col gap-4 hidden xl:flex">
				<form
					className="hidden"
					ref={redirectFormRef}
					id="redirect-form-bottom"
					method="post"
					action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
				/>
				<div className="flex justify-between gap-4">
					<Image
						src={'/images/slippers.png'}
						width={278}
						height={160}
						alt={'slippers image'}
					/>
					<Image
						src={'/images/logo-big.png'}
						width={160}
						height={160}
						alt={'BHP logo'}
						className="border rounded-4xl border-text-200"
					/>
				</div>
				<div className="bg-gradient-to-b from-[#78DBC7] to-[#006D79] rounded-4xl px-8 py-[3.5rem] min-h-[475px] relative">
					<Title
						level="2"
						color="white"
						size="4xl"
						className="font-extrabold uppercase mb-4 text-center"
					>
						<TranslateString
							i18nKey={'bestPartners'}
							context={'Main.travelInfo'}
						/>
					</Title>
					<div className="text-center text-white font-medium">
						{t('description')}
					</div>
					<Image
						src={'/images/partners-front-logo.png'}
						width={545}
						height={300}
						style={{ height: 'auto' }}
						alt={'Partners logos '}
						className="absolute bottom-0 left-0 right-0 rounded-4xl"
						unoptimized
					/>
				</div>
			</div>
			<div className="grow flex flex-col justify-start gap-4">
				{/* <TravelInformationSlider /> */}
				<TravelInformationBanner />
				<div className="xl:p-8 p-8 px-5 flex flex-col border border-text-200 rounded-4xl">
					<p className="text-text-900 font-extrabold uppercase mb-4 leading-none">
						{t('whyBHP')}
					</p>
					<div className="flex justify-between items-center">
						<div className="flex items-center ">
							<Icons.iconBadge className="me-1.5 shrink-0" />
							<span className="text-xs uppercase text-text-900 font-medium">
								<TranslateString
									i18nKey={'trustedBy'}
									context={'Main.travelInfo'}
								/>
							</span>
						</div>
						<div className="flex items-center">
							<Icons.save className="me-1.5 shrink-0" />
							<span className="text-xs uppercase text-text-900 font-medium">
								<TranslateString
									i18nKey={'compareSave'}
									context={'Main.travelInfo'}
								/>
							</span>
						</div>
						<div className="flex items-center">
							<Icons.priceAlert className="me-1.5 shrink-0" />
							<span className="text-xs uppercase text-text-900 font-medium">
								<TranslateString
									i18nKey={'smartPrice'}
									context={'Main.travelInfo'}
								/>
							</span>
						</div>
						<Button
							id="focus-form-button"
							big
							onClick={handleButtonClick}
							bgColor="main"
							textColor="white"
							name="Search hotels now"
							className="font-bold md:whitespace-nowrap hover:opacity-80 text-xl !bg-gradient-to-r h-14 px-6 w-fit from-main to-[#E02189] !rounded-full transition-all ease-linear flex items-center justify-center"
						>
							<Icons.iconLens className="mr-3 shrink-0 rtl:mr-0 rtl:ml-3" />
							<span className="inline">{getSearchText()}</span>
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default dynamic(() => Promise.resolve(TravelInformation), { ssr: false })